import type { UIMessage } from 'ai';
import { useCallback, useState } from 'react';
import { asToolPart } from './tool-parts';

/**
 * A graphic proposal's controls, as Claire names them when she tells the owner
 * what to do with the card (the generate-graphic skill: "Change images, Accept
 * and Reject"). Case-sensitive, so ordinary prose ("clients who accept…") is
 * never read as an instruction to press a button.
 */
const PROPOSAL_CONTROLS = /\b(?:Accept|Reject|Change images)\b/;

/** A markdown divider on its own (`---`, `***`, `___`). */
const DIVIDER = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

interface TurnCard {
  toolCallId: string;
  /** Lower-cased, for matching against the reply; null when the card has none. */
  title: string | null;
}

/** The cards a turn shows: tool parts whose output carries a typed presentation. */
function cardsIn(parts: UIMessage['parts']): TurnCard[] {
  const cards: TurnCard[] = [];
  for (const part of parts) {
    const presentation = asToolPart(part)?.output?.presentation;
    if (!presentation || typeof presentation !== 'object') continue;
    const { type, title } = presentation as { type?: unknown; title?: unknown };
    if (typeof type !== 'string' || type === 'none') continue;
    cards.push({
      toolCallId: (part as { toolCallId: string }).toolCallId,
      title:
        typeof title === 'string' && title.trim()
          ? title.trim().toLowerCase()
          : null,
    });
  }
  return cards;
}

/**
 * A turn's reply text, minus the instruction that pointed at an answered graphic
 * proposal's controls.
 *
 * Claire writes "…then hit Accept to generate it" before the owner answers the
 * card. Once it is answered, the card says what happened and that line points
 * at a button that is gone. One stored row holds the whole turn's text —
 * an answer to another question, another card's instructions — so only the
 * paragraphs about the failed card's controls go:
 *
 * - a paragraph that does not name Accept, Reject or Change images stays;
 * - one that names another card in the turn stays;
 * - one that names an answered card goes;
 * - one that names no card goes only when the answered proposals are the turn's
 *   only cards — otherwise it may be another card's instruction, so it stays.
 *
 * Returns null when nothing is left to show. Display only: the stored message
 * is untouched, since Claude signs its thinking over earlier turns.
 */
export function withoutStaleProposalReply(
  text: string,
  parts: UIMessage['parts'],
  answeredToolCallIds: ReadonlySet<string>
): string | null {
  if (answeredToolCallIds.size === 0) return text;
  const cards = cardsIn(parts);
  const others = cards.filter((c) => !answeredToolCallIds.has(c.toolCallId));
  const titlesOf = (list: TurnCard[]) =>
    list.flatMap((c) => (c.title ? [c.title] : []));
  const answeredTitles = titlesOf(
    cards.filter((c) => answeredToolCallIds.has(c.toolCallId))
  );
  const otherTitles = titlesOf(others);
  const names = (paragraph: string, titles: string[]) =>
    titles.some((title) => paragraph.toLowerCase().includes(title));

  const paragraphs = text.split(/\n[ \t]*\n/);
  const kept = paragraphs.filter(
    (paragraph) =>
      !PROPOSAL_CONTROLS.test(paragraph) ||
      names(paragraph, otherTitles) ||
      (others.length > 0 && !names(paragraph, answeredTitles))
  );
  if (kept.length === paragraphs.length) return text;
  // Claire often sets the stale line off with a divider; once the line is
  // gone, a divider left at either end separates nothing.
  while (kept.length > 0 && DIVIDER.test(kept[kept.length - 1])) kept.pop();
  while (kept.length > 0 && DIVIDER.test(kept[0])) kept.shift();
  return kept.some((p) => p.trim()) ? kept.join('\n\n') : null;
}

/**
 * Per message bubble: the graphic proposals that have been answered, and the reply
 * text to show given them. Shared by the panel, the full-page route and the
 * compact sheet so all three hide the same lines.
 */
export function useStaleProposalReply(parts: UIMessage['parts']) {
  const [answered, setAnswered] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const markReplyStale = useCallback((toolCallId: string) => {
    setAnswered((current) =>
      current.has(toolCallId) ? current : new Set(current).add(toolCallId)
    );
  }, []);
  return {
    markReplyStale,
    visibleReply: (text: string) =>
      withoutStaleProposalReply(text, parts, answered),
  };
}
